// Stateful "onboarding mode" flag for the demo.
//
// The real app sends a freshly-registered user (no company) into the join/
// onboarding flow; once they create a company + connect a bank they land on the
// dashboard. The demo has no real auth, so we fake that lifecycle with a flag:
//
//   • Demo logout      → startDemoOnboarding()  → getUserContext returns a
//     registered-user-only context → the role guard routes to the onboarding
//     first step (/join/company-lookup).
//   • Create company   → finishDemoOnboarding() → getUserContext returns the
//     full context again, so the rest of the flow (connect bank / skip) lands
//     back on the populated dashboard.
//
// Storage: sessionStorage, NOT localStorage. The flag only needs to survive one
// full-page navigation — the logout jump to /join/company-lookup (and an F5
// during the flow) — and sessionStorage does that within the tab. Unlike
// localStorage it dies with the tab, so an abandoned onboarding can never
// permanently lock a visitor out of the demo. (The original localStorage
// version did exactly that: log out, leave mid-onboarding, come back later →
// still "no company" → stuck in onboarding until all site data was wiped.)

const ONBOARDING_KEY = "fidoo-demo-onboarding";

// Called once at demo boot (index.tsx), before anything queries the flag.
export function initDemoOnboarding(): void {
  // Heal visitors stuck by the earlier localStorage-based flag — pre-fix builds
  // persisted it forever, showing onboarding on every subsequent visit.
  try {
    window.localStorage.removeItem(ONBOARDING_KEY);
  } catch {
    // ignore
  }
  // Self-heal within the tab: onboarding mode is only meaningful while the tab
  // is inside the /join flow (logout boots the page there; F5 during the flow
  // stays there). A fresh boot anywhere else means the visitor navigated to
  // the demo anew — drop the flag so they get the dashboard, not a surprise
  // onboarding they may not know how to finish.
  try {
    if (!window.location.pathname.startsWith("/join")) {
      window.sessionStorage.removeItem(ONBOARDING_KEY);
    }
  } catch {
    // ignore
  }
}

export function isDemoOnboardingActive(): boolean {
  try {
    return window.sessionStorage.getItem(ONBOARDING_KEY) === "1";
  } catch {
    return false;
  }
}

export function startDemoOnboarding(): void {
  try {
    window.sessionStorage.setItem(ONBOARDING_KEY, "1");
  } catch {
    // Private mode / storage disabled — onboarding just won't trigger.
  }
}

export function finishDemoOnboarding(): void {
  try {
    window.sessionStorage.removeItem(ONBOARDING_KEY);
  } catch {
    // ignore
  }
}
